import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { isNativeApp, nativePushLogin, nativePushLogout } from "@/lib/nativePush";
import { setToken, clearToken, hydrate as hydrateToken } from "@/lib/tokenStore";

const AuthContext = createContext(null);

// Cache clé de la dernière session valide. Permet d'hydrater instantanément
// `user`/`profile` au cold-start si le token est présent, évitant une
// redirection vers /login pendant que /auth/me est en vol (ou échoue de
// manière transitoire à cause du réseau/WKWebView).
const USER_CACHE_KEY = "price_user_cache_v1";
function readUserCache() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}
function writeUserCache(user, profile) {
  try {
    if (user) localStorage.setItem(USER_CACHE_KEY, JSON.stringify({ user, profile: profile || null }));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch (_) { /* ignore */ }
}

/** Identify (or de-identify) the current user with OneSignal so we can target
 *  push notifications by their backend `user.id` (External ID).
 *  - Web/PWA: uses OneSignalDeferred queue (Web SDK v16).
 *  - Capacitor Android/iOS: uses the native Cordova plugin via nativePush. */
function onesignalLogin(externalId) {
  if (!externalId) return;
  if (isNativeApp()) {
    nativePushLogin(externalId);
    return;
  }
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async function (OneSignal) {
    try { await OneSignal.login(String(externalId)); } catch (_) {}
  });
}
function onesignalLogout() {
  if (isNativeApp()) {
    nativePushLogout();
    return;
  }
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async function (OneSignal) {
    try { await OneSignal.logout(); } catch (_) {}
  });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (opts = {}) => {
    const { hadStoredToken = false, _attempt = 0 } = opts;
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setProfile(data.profile);
      writeUserCache(data.user, data.profile);
      if (data?.access_token) {
        await setToken(data.access_token);
      }
      if (data?.user?.id) onesignalLogin(data.user.id);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        // Cold-start protection : si on avait bien un token stocké, on
        // retente UNE FOIS après 1s. Capacitor peut parfois lancer la
        // requête avant que le token localStorage soit finalisé. On ne
        // vire la session qu'après confirmation d'un vrai 401.
        if (hadStoredToken && !opts._alreadyRetried) {
          await new Promise((r) => setTimeout(r, 1000));
          return refresh({ hadStoredToken, _alreadyRetried: true });
        }
        // 401 confirmé → session expirée pour de vrai. On purge tout.
        await clearToken();
        writeUserCache(null);
        setUser(null);
        setProfile(null);
      } else if (hadStoredToken && _attempt < 4) {
        // Erreur réseau / 5xx transitoire au cold-start alors qu'on a un
        // token valide en cache. On retente avec backoff (0.5s, 1s, 2s,
        // 4s) SANS jamais vider la session. Le user reste connecté via
        // le cache local pour que ProtectedRoute ne redirige pas.
        const delay = 500 * Math.pow(2, _attempt);
        await new Promise((r) => setTimeout(r, delay));
        return refresh({ hadStoredToken, _attempt: _attempt + 1, _alreadyRetried: opts._alreadyRetried });
      }
      // Après épuisement des retries : on garde l'état user précédent
      // (hydraté depuis le cache dans useEffect) → l'app reste utilisable
      // hors-ligne / en cas de coupure réseau au boot.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Sur natif, on restaure d'abord le token depuis Capacitor Preferences
    // (Keychain iOS / EncryptedSharedPrefs Android) avant tout appel API :
    // le localStorage de la WebView peut avoir été purgé par le système
    // pendant que l'app était en background.
    (async () => {
      let hydratedToken = null;
      try { hydratedToken = await hydrateToken(); } catch (_) { /* ignore */ }
      // Hydrate immédiatement `user`/`profile` depuis le cache si un token
      // existe. Comme ça, ProtectedRoute voit un user valide et n'envoie
      // pas l'utilisateur sur /login pendant que /auth/me est en cours
      // (ou en cas d'échec réseau transitoire).
      if (hydratedToken) {
        const cached = readUserCache();
        if (cached?.user) {
          setUser(cached.user);
          setProfile(cached.profile || null);
        }
      }
      await refresh({ hadStoredToken: !!hydratedToken });
    })();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) await setToken(data.access_token);
    setUser(data.user);
    if (data?.user?.id) onesignalLogin(data.user.id);
    await refresh();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    await clearToken();
    writeUserCache(null);
    setUser(null);
    setProfile(null);
    onesignalLogout();
  };

  const setSession = async (data) => {
    if (data?.access_token) await setToken(data.access_token);
    if (data?.user) setUser(data.user);
    if (data?.user?.id) onesignalLogin(data.user.id);
    await refresh();
  };

  return (
    <AuthContext.Provider value={{ user, profile, scope: user?.scope || "full", loading, login, logout, refresh, setSession, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
